/**
 * User Feedback Service for submitting product feedback from the AI area
 */

import { defaultErrorHandler as commonErrorHandler, createAbortSafeErrorHandler, createHeaders, getApiUrls } from './serviceConfig';

export type FeedbackCategory = 'SUGGESTION' | 'ERROR' | 'OTHER';

export type SubmitFeedbackPayload = {
  category: FeedbackCategory;
  comment: string;
};

export type SubmitFeedbackResponse = {
  createdAt: string;
  id: string;
};

export type SubmitFeedbackOptions = {
  signal?: AbortSignal;
};

type FeedbackServiceConfig = {
  onError?: (error: unknown) => void;
};

type FeedbackService = {
  submitFeedback: (payload: SubmitFeedbackPayload, options?: SubmitFeedbackOptions) => Promise<SubmitFeedbackResponse>;
};

/**
 * Creates a configured feedback service
 */
export function createFeedbackService(config: FeedbackServiceConfig = {}): FeedbackService {
  const configuredErrorHandler = config.onError || ((error: unknown) => commonErrorHandler('Feedback service', error as Error));

  const defaultErrorHandler = createAbortSafeErrorHandler(configuredErrorHandler) as (error: unknown) => void;

  const submitFeedback = async ({ category, comment }: SubmitFeedbackPayload, { signal }: SubmitFeedbackOptions = {}): Promise<SubmitFeedbackResponse> => {
    try {
      const urls = getApiUrls();
      const response = await fetch(urls.feedback.submit(), {
        method: 'POST',
        headers: createHeaders(),
        body: JSON.stringify({ category, comment }),
        signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to submit feedback: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  return { submitFeedback };
}

export default createFeedbackService();
